'use client';

import { useToastHelpers } from '@/hooks/useToastHelpers';

export default function ToastTestPage() {
    const { success, error, info, warn } = useToastHelpers();

    const handleTestSuccess = () => {
        success('This is a success message!', 'Success');
    };

    const handleTestError = () => {
        error('This is an error message!', 'Error');
    };

    const handleTestInfo = () => {
        info('This is an info message!', 'Info');
    };

    const handleTestWarning = () => {
        warn('This is a warning message!', 'Warning');
    };

    const handleTestGlobal = () => {
        success('Using global fallback!', 'Global Test');
    };

    const handleTestMultiple = () => {
        success('Toast 1', 'Success');
        setTimeout(() => error('Toast 2', 'Error'), 200);
        setTimeout(() => info('Toast 3', 'Info'), 400);
        setTimeout(() => warn('Toast 4', 'Warning'), 600);
    };

    const handleTestQueue = () => {
        for (let i = 1; i <= 8; i++) {
            setTimeout(() => {
                info(`Toast ${i} of 8`, `Test ${i}`);
            }, i * 100);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold text-gray-900 mb-8">
                    Toast System Test Page
                </h1>

                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">
                        Individual Variants
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <button
                            type="button"
                            onClick={handleTestSuccess}
                            className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Success Toast
                        </button>
                        <button
                            type="button"
                            onClick={handleTestError}
                            className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            Error Toast
                        </button>
                        <button
                            type="button"
                            onClick={handleTestInfo}
                            className="px-4 py-3 bg-brand-dark text-white rounded-lg hover:bg-brand-dark transition-colors"
                        >
                            Info Toast
                        </button>
                        <button
                            type="button"
                            onClick={handleTestWarning}
                            className="px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                        >
                            Warning Toast
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">
                        Advanced Tests
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <button
                            type="button"
                            onClick={handleTestGlobal}
                            className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                            Global Fallback
                        </button>
                        <button
                            type="button"
                            onClick={handleTestMultiple}
                            className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Multiple Toasts
                        </button>
                        <button
                            type="button"
                            onClick={handleTestQueue}
                            className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            Test Queue (8)
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4">
                        Expected Behavior
                    </h2>
                    <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-600 mt-2 mr-3" />
                            <span>
                                <strong>Success:</strong> Green background
                                (bg-green-50), green border and dot
                            </span>
                        </li>
                        <li className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-red-600 mt-2 mr-3" />
                            <span>
                                <strong>Error:</strong> Red background
                                (bg-red-50), red border and dot
                            </span>
                        </li>
                        <li className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-brand mt-2 mr-3" />
                            <span>
                                <strong>Info:</strong> Orange background
                                (bg-brand/5), orange border and dot
                            </span>
                        </li>
                        <li className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mt-2 mr-3" />
                            <span>
                                <strong>Warning:</strong> Yellow background
                                (bg-yellow-50), yellow border and dot
                            </span>
                        </li>
                        <li className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-gray-600 mt-2 mr-3" />
                            <span>
                                <strong>Positioning:</strong> Toasts appear
                                below navbar with dynamic offset
                            </span>
                        </li>
                        <li className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-gray-600 mt-2 mr-3" />
                            <span>
                                <strong>Queue:</strong> Max 6 visible toasts,
                                "+N more" badge appears when queue overflow
                            </span>
                        </li>
                        <li className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-gray-600 mt-2 mr-3" />
                            <span>
                                <strong>Timer:</strong> Countdown shows
                                remaining seconds, progress bar animates
                            </span>
                        </li>
                    </ul>
                </div>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">
                        Debugging Tips:
                    </h3>
                    <ol className="list-decimal list-inside space-y-1 text-blue-800 text-sm">
                        <li>Open browser DevTools Console to see debug logs</li>
                        <li>
                            Check if toast context is available (logged on
                            mount)
                        </li>
                        <li>
                            Look for toasts in top-center of screen (below
                            navbar)
                        </li>
                        <li>
                            Toasts should have colored backgrounds and gradient
                            progress bars
                        </li>
                        <li>
                            Check Network tab if toasts not appearing (JS
                            errors)
                        </li>
                    </ol>
                </div>
            </div>
        </div>
    );
}
